import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Aide ⓘ — une seule bulle custom (pas de title natif navigateur = double tooltip).
 * Portal + pointer-events-auto : visible malgré overflow/truncate, hors du bouton de navigation KPI.
 */
export default function AnalyticsInfoHint({ text, label = 'Aide' }) {
  const id = useId();
  const btnRef = useRef(null);
  const tipRef = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 8, width: 224 });

  const updatePosition = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(224, Math.max(160, window.innerWidth - 16));
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    let top = r.bottom + 6;
    if (top + 120 > window.innerHeight && r.top > 140) {
      top = Math.max(8, r.top - 8);
    }
    setCoords({ top, left, width });
  };

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openTip = () => {
    clearClose();
    updatePosition();
    setOpen(true);
  };

  const scheduleClose = () => {
    clearClose();
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  const stopNav = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => () => clearClose(), []);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || tipRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [open]);

  if (!text) return null;

  return (
    <span
      className="relative inline-flex align-middle pointer-events-auto"
      data-testid="analytics-info-hint"
    >
      <button
        ref={btnRef}
        type="button"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-[10px] font-semibold text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={openTip}
        onMouseLeave={scheduleClose}
        onFocus={openTip}
        onBlur={scheduleClose}
        onPointerDown={stopNav}
        onClick={(e) => {
          stopNav(e);
          setOpen((v) => {
            const next = !v;
            if (next) updatePosition();
            return next;
          });
        }}
      >
        i
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <span
              ref={tipRef}
              id={id}
              role="tooltip"
              data-testid="analytics-info-tooltip"
              onMouseEnter={openTip}
              onMouseLeave={scheduleClose}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                width: coords.width,
                zIndex: 9999,
              }}
              className="whitespace-normal break-words rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left text-[11px] leading-snug text-gray-700 shadow-lg"
            >
              {text}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}
